import React from 'react';
import classNames from 'classnames';
import track from 'react-tracking';
import { Helmet } from 'react-helmet-async';

import paragonBreakpoints from '@edx/paragon/dist/utils/breakpoints';
import {
  addCloudflareImageParams, generateUrlForBreakpoints, generateSrcSet, ImageOptions, Breakpoint,
} from './utils';

interface RawImageProps {
  className?: string;
  imgUrl: string;
  lazyLoad?: boolean;
  description?: string;
  title?: string;
  width?: number | 'auto';
  height?: number | 'auto';
}

interface CloudflareImageProps extends Omit<RawImageProps, 'imgUrl'> {
  url: string;
  additionalParams?: ImageOptions;
  breakpoints?: Breakpoint[];
  isLcp?: boolean;
}

function RawImage(props: RawImageProps) {
  const {
    className, imgUrl, lazyLoad, description, title, width, height,
  } = props;

  return (
    <img
      className={classNames('CloudflareImage', className)}
      src={imgUrl}
      loading={lazyLoad ? 'lazy' : 'eager'}
      alt={description || title || ''}
      width={width}
      height={height}
    />
  );
}

const defaultImageFormat = 'webp';

function CloudflareImage(props: CloudflareImageProps) {
  if (!props.url) {
    return null;
  }

  const {
    url,
    width,
    height,
    additionalParams,
    breakpoints,
    isLcp,
  } = props;
  const params = {
    width,
    height,
    ...additionalParams,
  };

  const format = additionalParams?.format || defaultImageFormat;
  const imgUrl = addCloudflareImageParams(url, { ...params, format });

  const breakpointsWithUrl = generateUrlForBreakpoints(breakpoints, url, format);
  const srcSet = generateSrcSet(breakpointsWithUrl);

  return (
    <>
      {isLcp && imgUrl && (
        <Helmet>
          <link href={imgUrl} as="image" />
          {breakpointsWithUrl && breakpointsWithUrl.map(({ url: src, type }) => {
            let mediaQuery = `(min-width: ${paragonBreakpoints[type].minWidth}px) and (max-width: ${paragonBreakpoints[type].maxWidth}px)`;
            if (!paragonBreakpoints[type].minWidth) {
              mediaQuery = `(max-width: ${paragonBreakpoints[type].maxWidth}px)`;
            }
            if (!paragonBreakpoints[type].maxWidth) {
              mediaQuery = `(min-width: ${paragonBreakpoints[type].minWidth}px)`;
            }
            return (
              <link href={src} as="image" media={mediaQuery} />
            );
          })}
        </Helmet>
      )}

      {srcSet && (
        <picture>
          <source srcSet={srcSet} />
          <RawImage {...props} imgUrl={imgUrl} />
        </picture>
      )}

      {!srcSet && (
        <RawImage {...props} imgUrl={imgUrl} />
      )}
    </>
  );
}

CloudflareImage.defaultProps = {
  additionalParams: {
    format: 'webp',
    quality: 75,
  },
  breakpoints: [],
  isLcp: false,
};
RawImage.defaultProps = {
  className: '',
  lazyLoad: true,
  description: '',
  title: '',
  width: 'auto',
  height: 'auto',
};

export default track({
  component: 'cloudflare-image',
})(CloudflareImage);
